/**
 * One issue: what was found, what it looked like, and everything said about it since.
 *
 * The page is a conversation between two people, so it is built as one — the note at the top
 * is what Namık wrote when he filed it, the log underneath is what happened after, and the
 * control at the bottom does both halves of the only act available here: move the status, say
 * why. Neither alone: a status change with no word on it is what makes a tracker useless six
 * weeks later, and a reply that leaves an issue reading *open* after it was fixed is worse.
 *
 * The note and the log are `white-space: pre-wrap` and **nothing more**. There is no markdown
 * renderer here on purpose (`docs/dev/admin/02`, "Nothing else"): a renderer is a dependency,
 * an XSS surface and a formatting argument, in exchange for bold text in a note two people
 * read.
 */

import {
  Button,
  EmptyState,
  FramedWindow,
  SelectField,
  StatusBadge,
  SurfaceHeader,
  TextAreaField,
  TextLink,
} from '@atlas-nook/design-system';
import { useState } from 'react';
import type { JSX } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';

import { adminEnv } from '../env.js';
import { useIssue } from '../data/queries.js';
import { appendIssueLog } from '../data/writes.js';
import { ISSUE_STATUSES } from '../data/issues.js';
import type { Issue, IssueLogEntry, IssueStatus } from '../data/issues.js';
import { RenderImage } from '../globals/RenderImage.js';
import { useToast } from '../shell/ToastProvider.js';
import { ADMIN_STRINGS as S } from '../strings.js';
import { CopyPromptButton } from './CopyPromptButton.js';
import { fixPromptFor } from './fixPrompt.js';
import { issueBadge } from './IssuesList.js';

/** A Firestore timestamp as a person reads it. `null` is a write still in flight. */
function when(at: { toDate: () => Date } | null): string {
  if (at === null) return '…';
  return at.toDate().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

export function IssueDetail(): JSX.Element {
  const { id } = useParams();
  const { data: issue, loading } = useIssue(id);

  if (id === undefined) return <Navigate to="/issues" replace />;
  if (issue === null) return loading ? <></> : <EmptyState title={S.issue.notFound} />;
  // Keyed on the id so opening a second issue never carries the first one's draft reply.
  return <IssueView key={issue.id} issue={issue} />;
}

function IssueView({ issue }: { issue: Issue }): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [status, setStatus] = useState<IssueStatus>(issue.status);
  const [reply, setReply] = useState('');
  const [working, setWorking] = useState(false);

  const moved = status !== issue.status;
  const said = reply.trim() !== '';

  async function post(): Promise<void> {
    if (!moved && !said) {
      toast.show(S.issue.nothingToPost);
      return;
    }

    setWorking(true);
    try {
      // A bare status change still writes a line, so the log is the whole history and not a
      // partial one you have to read the fields to complete.
      const text = said
        ? reply.trim()
        : `${S.issues.status[issue.status]} → ${S.issues.status[status]}`;
      await appendIssueLog(issue.id, { text, ...(moved ? { status } : {}) });
      setReply('');
      toast.show(S.issue.posted);
    } catch {
      toast.show(S.common.failed);
    } finally {
      setWorking(false);
    }
  }

  return (
    <>
      <SurfaceHeader
        kicker={S.issue.kicker}
        title={issue.title}
        meta={`${S.issue.meta(issue.id, S.issues.area[issue.area], S.issues.kind[issue.kind])}${
          issue.step === undefined ? '' : ` · ${S.issue.step(issue.step)}`
        } · ${S.issue.filed(when(issue.createdAt))}`}
        actions={
          <>
            <TextLink onClick={() => void navigate('/issues')}>{S.nav.issues}</TextLink>
            <StatusBadge status={issueBadge(issue.status)}>
              {S.issues.status[issue.status]}
            </StatusBadge>
            {/* The loop's first step, at the top of the page it starts from — both lanes,
                quick first, because quick is the common case. */}
            <CopyPromptButton
              text={fixPromptFor(issue, adminEnv(), 'quick')}
              label={S.issues.copyPromptFor(issue.id)}
            />
            <CopyPromptButton
              text={fixPromptFor(issue, adminEnv(), 'thorough')}
              label={S.issues.copyPromptThoroughFor(issue.id)}
            >
              {S.issues.copyPromptThorough}
            </CopyPromptButton>
          </>
        }
      />

      <section className="fsec">
        <div className="panelhead">
          <span className="ph-t">{S.issue.note}</span>
        </div>
        <p className={issue.note === undefined ? 'note' : 'itext'}>
          {issue.note ?? S.issue.noNote}
        </p>
      </section>

      <section className="fsec">
        <div className="panelhead">
          <span className="ph-t">{S.issue.shots}</span>
        </div>
        {issue.screenshots === undefined ? (
          <p className="note">{S.issue.shotsNone}</p>
        ) : (
          <div className="ishots">
            {issue.screenshots.map((path) => (
              <FramedWindow key={path} aspectRatio="16 / 10" caption={path}>
                <RenderImage path={path} alt="" />
              </FramedWindow>
            ))}
          </div>
        )}
      </section>

      <section className="fsec">
        <div className="panelhead">
          <span className="ph-t">{S.issue.log}</span>
          <span className="ph-c">{S.issue.cliHint(issue.id)}</span>
        </div>
        {issue.log.length === 0 ? (
          <p className="note">{S.issue.logEmpty}</p>
        ) : (
          <ul className="ilog">
            {issue.log.map((entry, index) => (
              <LogLine key={`${index}-${entry.text}`} entry={entry} />
            ))}
          </ul>
        )}
      </section>

      <section className="fsec">
        <div className="panelhead">
          <span className="ph-t">{S.issue.say}</span>
        </div>
        <p className="note">{S.issue.sayHelp}</p>
        <div className="stack">
          <SelectField
            label={S.issue.statusField}
            value={status}
            onChange={(value) => setStatus(value as IssueStatus)}
          >
            {ISSUE_STATUSES.map((value) => (
              <option key={value} value={value}>
                {S.issues.status[value]}
              </option>
            ))}
          </SelectField>
          <TextAreaField label={S.issue.reply} rows={3} value={reply} onChange={setReply} />
          <div className="btnrow">
            <Button tone="primary" working={working} onClick={() => void post()}>
              {S.issue.post}
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}

function LogLine({ entry }: { entry: IssueLogEntry }): JSX.Element {
  return (
    <li>
      <span className="cost">{S.issue.logBy(S.issue.voice[entry.by], when(entry.at))}</span>
      <p className="itext">{entry.text}</p>
    </li>
  );
}
